import React from "react";

import { CoreService } from "@/services/core/CoreService";
import { Device, DeviceService } from "@/services/features/DeviceService";
import { Customer, Store, StoreService } from "@/services/features/StoreService";
import { getCurrentRegion } from "@/utils/environment";

import { normalizeCoordinates, StoreHealthCache } from "./commandCenter.types";
import { getStoreHealth } from "./commandCenterHealth";
import {
  clearCommandCenterSessionCache,
  type CommandCenterCacheScope,
  readCommandCenterSessionCache,
  writeCommandCenterSessionCache,
} from "./commandCenterSessionCache";

export const COMMAND_CENTER_UPDATE_INTERVAL_MS = 250;
export const COMMAND_CENTER_CACHE_INTERVAL_MS = 2_000;

const eligibleStore = (store: Store) => store.group?.trim().toUpperCase() !== "DEMO";
export const getReportableDevices = (devices: Device[]): Device[] =>
  devices.filter((device) => !device.ignored);
const normalizeStore = (store: Store): Store => {
  const coordinates = normalizeCoordinates(store);
  return {
    ...store,
    geoPoint: coordinates
      ? { lat: coordinates.latitude, lon: coordinates.longitude }
      : { lat: Number.NaN, lon: Number.NaN },
  };
};

type CommandCenterDataOptions = {
  priorityCustomerId?: number;
  priorityStoreId?: number;
  onStoreDeviceHealthLoadError?: (store: Store) => void;
};

export const useCommandCenterData = ({
  priorityCustomerId,
  priorityStoreId,
  onStoreDeviceHealthLoadError,
}: CommandCenterDataOptions = {}) => {
  const storeService = React.useMemo(() => StoreService.getInstance(), []);
  const deviceService = React.useMemo(() => DeviceService.getInstance(), []);
  const coreService = React.useMemo(() => CoreService.getInstance(), []);
  const generation = React.useRef(0);
  const cancelPendingLoad = React.useRef<(() => void) | null>(null);
  const priorityCustomerIdRef = React.useRef<number | undefined>(priorityCustomerId);
  const priorityStoreIdRef = React.useRef<number | undefined>(priorityStoreId);
  const storeDeviceHealthLoadErrorRef = React.useRef(onStoreDeviceHealthLoadError);
  const pendingJobsRef = React.useRef<Store[][]>([]);
  priorityCustomerIdRef.current = priorityCustomerId;
  priorityStoreIdRef.current = priorityStoreId;
  storeDeviceHealthLoadErrorRef.current = onStoreDeviceHealthLoadError;
  const [customers, setCustomers] = React.useState<Customer[]>([]);
  const [stores, setStores] = React.useState<Store[]>([]);
  const [healthByStoreId, setHealthByStoreId] = React.useState<Record<number, StoreHealthCache>>(
    {},
  );
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [initialError, setInitialError] = React.useState<string | null>(null);

  const loadDeviceHealth = React.useCallback(
    async ({
      activeGeneration,
      cacheScope,
      customers,
      initialHealthByStoreId,
      stores,
      targetStores,
      priorityCustomerId,
    }: {
      activeGeneration: number;
      cacheScope: CommandCenterCacheScope;
      customers: Customer[];
      initialHealthByStoreId: Record<number, StoreHealthCache>;
      stores: Store[];
      targetStores: Store[];
      priorityCustomerId?: number;
    }) => {
      const customerNameById = new Map(customers.map((customer) => [customer.id, customer.name]));
      const byCustomer = new Map<number, Store[]>();
      targetStores.forEach((store) => {
        const owned = byCustomer.get(store.customerId) ?? [];
        owned.push(store);
        byCustomer.set(store.customerId, owned);
      });
      const jobs = [...byCustomer.values()].sort(
        (a, b) =>
          a.length - b.length ||
          (customerNameById.get(a[0]?.customerId) ?? "").localeCompare(
            customerNameById.get(b[0]?.customerId) ?? "",
          ),
      );
      const queue = [...jobs];
      const priorityJobIndex = queue.findIndex((job) => job[0]?.customerId === priorityCustomerId);
      if (priorityJobIndex > 0) {
        const [priorityJob] = queue.splice(priorityJobIndex, 1);
        if (priorityJob) queue.unshift(priorityJob);
      }
      pendingJobsRef.current = queue;
      const nextHealthByStoreId = { ...initialHealthByStoreId };
      let updateTimer: ReturnType<typeof setTimeout> | undefined;
      let cacheTimer: ReturnType<typeof setTimeout> | undefined;
      let hasPendingUpdate = false;
      let hasPendingCache = false;
      const publish = () => {
        updateTimer = undefined;
        if (!hasPendingUpdate || activeGeneration !== generation.current) return;
        hasPendingUpdate = false;
        const snapshot = { ...nextHealthByStoreId };
        React.startTransition(() => setHealthByStoreId(snapshot));
      };
      const persist = () => {
        cacheTimer = undefined;
        if (!hasPendingCache || activeGeneration !== generation.current) return;
        hasPendingCache = false;
        writeCommandCenterSessionCache(cacheScope, {
          customers,
          stores,
          healthByStoreId: nextHealthByStoreId,
        });
      };
      const scheduleUpdate = () => {
        hasPendingUpdate = true;
        updateTimer ??= setTimeout(publish, COMMAND_CENTER_UPDATE_INTERVAL_MS);
      };
      const scheduleCache = () => {
        hasPendingCache = true;
        cacheTimer ??= setTimeout(persist, COMMAND_CENTER_CACHE_INTERVAL_MS);
      };
      const flush = () => {
        clearTimeout(updateTimer);
        clearTimeout(cacheTimer);
        updateTimer = undefined;
        cacheTimer = undefined;
        publish();
        persist();
      };
      const cancel = () => {
        flush();
        window.removeEventListener("pagehide", flush);
      };
      cancelPendingLoad.current = cancel;
      // Preserve the last partial checkpoint when leaving during loading.
      window.addEventListener("pagehide", flush);
      const runWorker = async () => {
        while (queue.length && activeGeneration === generation.current) {
          const job = queue.shift();
          if (!job) return;
          for (const store of job) {
            if (activeGeneration !== generation.current) return;
            nextHealthByStoreId[store.id] = { state: "loading" };
            scheduleUpdate();
            const response = await deviceService.getDevicesByStoreId(store.id, false);
            if (activeGeneration !== generation.current) return;
            if (response.success) {
              const allDevices = response.body ?? [];
              const devices = getReportableDevices(allDevices);
              const ignoredDevices = allDevices.filter((device) => device.ignored);
              const health = getStoreHealth(devices, store.timezone);
              nextHealthByStoreId[store.id] = {
                state: "complete",
                completedAt: Date.now(),
                devices,
                ignoredDevices,
                health,
              };
            } else {
              nextHealthByStoreId[store.id] = {
                state: "error",
                completedAt: Date.now(),
                error: response.error?.message,
              };
              storeDeviceHealthLoadErrorRef.current?.(store);
            }
            scheduleUpdate();
            scheduleCache();
          }
        }
      };
      try {
        // Retain the existing three-customer limit so batching does not increase backend load.
        await Promise.all(Array.from({ length: Math.min(3, jobs.length) }, runWorker));
      } finally {
        cancel();
        if (cancelPendingLoad.current === cancel) cancelPendingLoad.current = null;
        if (activeGeneration === generation.current) pendingJobsRef.current = [];
      }
    },
    [deviceService],
  );

  const prioritizeCustomer = React.useCallback((customerId: number | undefined) => {
    priorityCustomerIdRef.current = customerId;
    if (!customerId) return;
    const priorityJobIndex = pendingJobsRef.current.findIndex(
      (job) => job[0]?.customerId === customerId,
    );
    if (priorityJobIndex > 0) {
      const [priorityJob] = pendingJobsRef.current.splice(priorityJobIndex, 1);
      if (priorityJob) pendingJobsRef.current.unshift(priorityJob);
    }
  }, []);

  const refresh = React.useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      cancelPendingLoad.current?.();
      const activeGeneration = ++generation.current;
      const cacheScope: CommandCenterCacheScope = {
        region: getCurrentRegion(),
        userId: coreService.getStoredUser()?.id,
      };
      setIsRefreshing(true);
      if (force) {
        clearCommandCenterSessionCache(cacheScope);
      } else {
        const cached = readCommandCenterSessionCache(cacheScope);
        if (cached) {
          setCustomers(cached.customers);
          setStores(cached.stores);
          setHealthByStoreId(cached.healthByStoreId);
          setInitialError(null);
          setIsInitialLoading(false);
          const storesMissingHealth = cached.stores.filter(
            (store) => cached.healthByStoreId[store.id]?.state !== "complete",
          );
          if (storesMissingHealth.length) {
            await loadDeviceHealth({
              activeGeneration,
              cacheScope,
              customers: cached.customers,
              initialHealthByStoreId: cached.healthByStoreId,
              stores: cached.stores,
              targetStores: storesMissingHealth,
              priorityCustomerId:
                cached.stores.find((store) => store.id === priorityStoreIdRef.current)
                  ?.customerId ?? priorityCustomerIdRef.current,
            });
          }
          if (activeGeneration === generation.current) setIsRefreshing(false);
          return;
        }
      }
      setIsInitialLoading(true);
      setInitialError(null);
      setHealthByStoreId({});
      try {
        const customerResponse = await storeService.getCustomers();
        if (activeGeneration !== generation.current) return;
        const storeResponse = await storeService.getStores();
        if (activeGeneration !== generation.current) return;
        const nextStores = (storeResponse.data ?? [])
          .map(normalizeStore)
          .filter(eligibleStore)
          .sort((a, b) => a.name.localeCompare(b.name));
        const storeCustomerIds = new Set(nextStores.map((store) => store.customerId));
        const nextCustomers = (customerResponse.data ?? [])
          .filter((customer) => storeCustomerIds.has(customer.id))
          .sort((a, b) => a.name.localeCompare(b.name));
        const selectedCustomerId =
          nextStores.find((store) => store.id === priorityStoreIdRef.current)?.customerId ??
          priorityCustomerIdRef.current;
        setStores(nextStores);
        setCustomers(nextCustomers);
        setIsInitialLoading(false);
        writeCommandCenterSessionCache(cacheScope, {
          customers: nextCustomers,
          stores: nextStores,
          healthByStoreId: {},
        });
        await loadDeviceHealth({
          activeGeneration,
          cacheScope,
          customers: nextCustomers,
          initialHealthByStoreId: {},
          stores: nextStores,
          targetStores: nextStores,
          priorityCustomerId: selectedCustomerId,
        });
        if (activeGeneration === generation.current) setIsRefreshing(false);
      } catch (error) {
        if (activeGeneration === generation.current) {
          setInitialError(error instanceof Error ? error.message : "");
          setIsInitialLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [coreService, loadDeviceHealth, storeService],
  );

  React.useEffect(() => {
    void refresh();
    return () => {
      cancelPendingLoad.current?.();
      generation.current += 1;
    };
  }, [refresh]);
  return {
    customers,
    stores,
    healthByStoreId,
    isInitialLoading,
    isRefreshing,
    initialError,
    refresh,
    prioritizeCustomer,
  };
};
